import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import {
    ArrowLeft, ArrowRight, BadgeCheck, Building2, ExternalLink, Flame, Leaf, Loader2,
    Users, Zap, Trophy, Crown, Check, X, FileDown, LayoutList, Maximize2,
} from "lucide-react";
import SupplierAvatar from "@/components/SupplierAvatar";
import { useSiteContent } from "@/lib/useSiteContent";
import { keysForMany } from "@/config/cmsSections";

const AudienceIcon = { b2c: Users, b2b: Building2, both: BadgeCheck };

const RATING_FIELDS_DEF = [
    { key: "overall_rating", cmsKey: "compare.rating.overall", defaultLabel: "Note globale" },
    { key: "transparency_rating", cmsKey: "compare.rating.transparency", defaultLabel: "Transparence" },
    { key: "customer_service_rating", cmsKey: "compare.rating.service", defaultLabel: "Service client" },
    { key: "green_rating", cmsKey: "compare.rating.green", defaultLabel: "Énergie verte" },
    { key: "price_rating", cmsKey: "compare.rating.price", defaultLabel: "Prix" },
    { key: "digital_rating", cmsKey: "compare.rating.digital", defaultLabel: "Digital" },
];

function Bar({ value, isBest }) {
    const width = `${(Number(value) / 5) * 100}%`;
    return (
        <div className="space-y-1">
            <div className="flex items-center justify-between">
                <span className={`font-mono font-bold text-base ${isBest ? "text-primary" : "text-foreground"}`}>
                    {Number(value).toFixed(1)}
                    <span className="text-xs text-muted-foreground">/5</span>
                </span>
                {isBest && <Crown className="h-4 w-4 text-accent fill-accent" />}
            </div>
            <div className="h-1.5 rounded-full bg-secondary overflow-hidden">
                <div className={`h-full rounded-full ${isBest ? "bg-accent" : "bg-primary"}`} style={{ width }} />
            </div>
        </div>
    );
}

function BoolCell({ value }) {
    return value ? (
        <Check className="h-5 w-5 text-primary" />
    ) : (
        <X className="h-5 w-5 text-muted-foreground/40" />
    );
}

function PriceCell({ value }) {
    return value != null ? (
        <span className="font-mono font-bold text-primary">{Number(value).toFixed(4)} <span className="text-xs text-muted-foreground">€/kWh</span></span>
    ) : (
        <span className="text-xs text-muted-foreground">—</span>
    );
}

export default function SuppliersCompare() {
    const [params] = useSearchParams();
    const c = useSiteContent(keysForMany("common", "suppliers-compare"));
    const ids = useMemo(() => (params.get("ids") || "").split(",").filter(Boolean), [params]);

    const AudienceLabel = {
        b2c: c("common.audience.b2c_plural", "Particuliers"),
        b2b: c("common.audience.b2b_plural", "Professionnels"),
        both: c("common.audience.both", "Particuliers · Pros"),
    };
    const PriceLabel = {
        1: c("compare.price.tier1", "€ Économique"),
        2: c("compare.price.tier2", "€€ Standard"),
        3: c("compare.price.tier3", "€€€ Premium"),
    };
    const RATING_FIELDS = RATING_FIELDS_DEF.map((f) => ({ ...f, label: c(f.cmsKey, f.defaultLabel) }));

    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [view, setView] = useState("detailed");
    const [downloading, setDownloading] = useState(false);

    const downloadPDF = async () => {
        setDownloading(true);
        try {
            const res = await api.get(`/suppliers/compare/pdf`, {
                params: { ids: ids.join(",") },
                responseType: "blob",
            });
            const blob = res.data;
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url;
            a.download = `energy-market-comparatif-${ids.join("-")}.pdf`;
            document.body.appendChild(a);
            a.click();
            a.remove();
            window.URL.revokeObjectURL(url);
            toast.success("PDF téléchargé");
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
        } finally {
            setDownloading(false);
        }
    };

    useEffect(() => {
        const load = async () => {
            if (ids.length === 0) {
                setItems([]);
                setLoading(false);
                return;
            }
            setLoading(true);
            try {
                const results = await Promise.all(
                    ids.map(async (slug) => {
                        const [s, o] = await Promise.all([
                            api.get(`/suppliers/${slug}`),
                            api.get(`/suppliers/${slug}/offers`),
                        ]);
                        return { ...s.data, offers: o.data };
                    })
                );
                setItems(results);
            } catch (e) {
                toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
            } finally {
                setLoading(false);
            }
        };
        load();
    }, [ids]);

    // Determine best value per rating field for highlighting
    const bestByField = useMemo(() => {
        const m = {};
        for (const f of RATING_FIELDS_DEF) {
            let bestVal = -Infinity;
            for (const it of items) {
                const v = Number(it[f.key]) || 0;
                if (v > bestVal) bestVal = v;
            }
            m[f.key] = bestVal;
        }
        return m;
    }, [items]);

    // Min price per energy
    const cheapestElec = useMemo(() => {
        let min = Infinity;
        for (const it of items) {
            const offs = (it.offers || []).filter((o) => o.energy_type === "electricity");
            for (const o of offs) if (o.price_per_kwh < min) min = o.price_per_kwh;
        }
        return min === Infinity ? null : min;
    }, [items]);

    const cheapestGas = useMemo(() => {
        let min = Infinity;
        for (const it of items) {
            const offs = (it.offers || []).filter((o) => o.energy_type === "gas");
            for (const o of offs) if (o.price_per_kwh < min) min = o.price_per_kwh;
        }
        return min === Infinity ? null : min;
    }, [items]);

    // Rating rows visible depending on the selected view.
    const visibleRatingFields = RATING_FIELDS.filter((f) => view === "detailed" || f.key === "overall_rating");

    if (loading) {
        return (
            <div className="em-container py-20 text-center text-muted-foreground" data-testid="compare-loading">
                <Loader2 className="h-8 w-8 mx-auto animate-spin" />
                <p className="mt-3">Préparation du comparatif…</p>
            </div>
        );
    }

    if (ids.length < 2 || items.length < 2) {
        return (
            <div className="em-container py-20" data-testid="compare-empty">
                <h1 className="font-display text-3xl md:text-4xl font-black text-primary">{c("compare.empty.title", "Sélection insuffisante")}</h1>
                <p className="text-muted-foreground mt-3 max-w-2xl">
                    {c("compare.empty.body", "Sélectionnez au moins 2 fournisseurs (maximum 4) depuis la page Fournisseurs pour les comparer côte-à-côte.")}
                </p>
                <Button asChild className="rounded-full mt-6 bg-primary hover:bg-primary/90" data-testid="compare-back-list">
                    <Link to="/fournisseurs"><ArrowLeft className="h-4 w-4 mr-2" />{c("compare.empty.cta", "Retour à la liste")}</Link>
                </Button>
            </div>
        );
    }

    const cols = items.length;
    // grid: label col + N supplier cols
    const gridStyle = { gridTemplateColumns: `220px repeat(${cols}, minmax(0, 1fr))` };

    return (
        <div className="em-container py-12" data-testid="compare-page">
            <Button asChild variant="ghost" size="sm" className="rounded-full mb-6" data-testid="compare-back">
                <Link to="/fournisseurs"><ArrowLeft className="h-4 w-4 mr-2" />{c("compare.back_label", "Tous les fournisseurs")}</Link>
            </Button>

            <p className="em-eyebrow">{c("compare.hero.eyebrow", "Comparaison côte-à-côte")}</p>
            <h1 className="font-display text-4xl md:text-5xl font-black tracking-tight text-primary mt-2">
                {items.map((i) => i.name).join(" · ")}
            </h1>
            <p className="text-muted-foreground mt-3 max-w-3xl">
                {c("compare.hero.legend", "La couronne signale le meilleur score par critère.")} <Crown className="inline h-3.5 w-3.5 text-accent fill-accent align-text-bottom" />
            </p>

            {/* CONTROLS */}
            <div className="mt-6 flex flex-wrap items-center justify-between gap-3" data-testid="compare-controls">
                <Tabs value={view} onValueChange={setView}>
                    <TabsList className="rounded-full">
                        <TabsTrigger value="compact" className="rounded-full gap-2" data-testid="view-compact">
                            <LayoutList className="h-4 w-4" />{c("compare.view.compact", "Vue compacte")}
                        </TabsTrigger>
                        <TabsTrigger value="detailed" className="rounded-full gap-2" data-testid="view-detailed">
                            <Maximize2 className="h-4 w-4" />{c("compare.view.detailed", "Vue détaillée")}
                        </TabsTrigger>
                    </TabsList>
                </Tabs>
                <Button
                    onClick={downloadPDF}
                    disabled={downloading}
                    type="button"
                    className="rounded-full bg-primary hover:bg-primary/90 font-bold"
                    data-testid="download-pdf-btn"
                >
                    {downloading ? (
                        <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Génération…</>
                    ) : (
                        <><FileDown className="h-4 w-4 mr-2" />{c("compare.pdf_cta", "Télécharger en PDF")}</>
                    )}
                </Button>
            </div>

            <div className="mt-10 overflow-x-auto border border-border rounded-3xl bg-card" data-testid="compare-grid">
                <div style={gridStyle} className="grid min-w-[760px]">
                    {/* HEADER */}
                    <div className="bg-secondary px-5 py-6 border-b border-border"></div>
                    {items.map((s) => (
                        <div key={`head-${s.id}`} className="bg-secondary px-5 py-6 border-b border-border border-l border-l-border" data-testid={`head-${s.slug}`}>
                            <Link to={`/fournisseurs/${s.slug}`} className="block group">
                                <div className="flex items-center gap-2 mb-2">
                                    <SupplierAvatar slug={s.slug} name={s.name} size="sm" className="shadow-sm" />
                                    <div>
                                        <h2 className="font-display text-lg font-bold text-primary group-hover:underline truncate">{s.name}</h2>
                                        <p className="text-xs text-muted-foreground truncate">{s.headquarters}</p>
                                    </div>
                                </div>
                                <p className="text-xs italic text-muted-foreground line-clamp-2">"{s.tagline}"</p>
                            </Link>
                            <div className="flex flex-wrap gap-1 mt-3">
                                {s.electricity && <Badge variant="outline" className="text-xs"><Zap className="h-3 w-3 mr-1" />Élec</Badge>}
                                {s.gas && <Badge variant="outline" className="text-xs"><Flame className="h-3 w-3 mr-1" />Gaz</Badge>}
                                {s.green_energy && <Badge className="text-xs bg-primary text-primary-foreground hover:bg-primary"><Leaf className="h-3 w-3 mr-1" />Verte</Badge>}
                            </div>
                        </div>
                    ))}

                    {/* RATINGS */}
                    {visibleRatingFields.map((f, fIdx) => (
                        <CompareRow key={f.key} label={f.label} isAlt={fIdx % 2 === 1}>
                            {items.map((s) => {
                                const v = s[f.key];
                                const isBest = Number(v) === bestByField[f.key];
                                return (
                                    <div key={`${s.id}-${f.key}`} className="px-5 py-4 border-l border-border" data-testid={`cell-${f.key}-${s.slug}`}>
                                        <Bar value={v} isBest={isBest} />
                                    </div>
                                );
                            })}
                        </CompareRow>
                    ))}

                    {/* AUDIENCE - detailed only */}
                    {view === "detailed" && (
                    <CompareRow label={c("compare.row.audience", "Cible")}>
                        {items.map((s) => {
                            const Icon = AudienceIcon[s.target_audience] || BadgeCheck;
                            return (
                                <div key={`aud-${s.id}`} className="px-5 py-4 border-l border-border text-sm flex items-center gap-2">
                                    <Icon className="h-4 w-4 text-muted-foreground" />
                                    {AudienceLabel[s.target_audience]}
                                </div>
                            );
                        })}
                    </CompareRow>
                    )}

                    {/* ENERGIES - detailed only (separated) */}
                    {/* GAZ - detailed only */}
                    {view === "detailed" && (
                    <CompareRow label={c("compare.row.elec", "Électricité")} isAlt>
                        {items.map((s) => (
                            <div key={`e-${s.id}`} className="px-5 py-4 border-l border-border"><BoolCell value={s.electricity} /></div>
                        ))}
                    </CompareRow>
                    )}
                    {view === "detailed" && (
                    <CompareRow label={c("compare.row.gas", "Gaz")}>
                        {items.map((s) => (
                            <div key={`g-${s.id}`} className="px-5 py-4 border-l border-border"><BoolCell value={s.gas} /></div>
                        ))}
                    </CompareRow>
                    )}
                    {view === "detailed" && (
                    <CompareRow label={c("compare.row.green", "100% verte")} isAlt>
                        {items.map((s) => (
                            <div key={`gr-${s.id}`} className="px-5 py-4 border-l border-border"><BoolCell value={s.green_energy} /></div>
                        ))}
                    </CompareRow>
                    )}

                    {/* PRICE TIER - detailed only */}
                    {view === "detailed" && (
                    <CompareRow label={c("compare.row.price_tier", "Positionnement prix")}>
                        {items.map((s) => (
                            <div key={`pt-${s.id}`} className="px-5 py-4 border-l border-border text-sm font-bold text-primary">
                                {PriceLabel[s.price_tier] || "—"}
                            </div>
                        ))}
                    </CompareRow>
                    )}

                    {/* CHEAPEST PRICES */}
                    <CompareRow label={c("compare.row.best_elec", "Meilleur prix élec (kWh)")} isAlt>
                        {items.map((s) => {
                            const elec = (s.offers || []).filter((o) => o.energy_type === "electricity");
                            const min = elec.length ? Math.min(...elec.map((o) => o.price_per_kwh)) : null;
                            const isBest = min !== null && cheapestElec !== null && min === cheapestElec;
                            return (
                                <div key={`pe-${s.id}`} className="px-5 py-4 border-l border-border">
                                    <div className="flex items-center gap-2">
                                        <PriceCell value={min} />
                                        {isBest && <Crown className="h-4 w-4 text-accent fill-accent" />}
                                    </div>
                                </div>
                            );
                        })}
                    </CompareRow>
                    <CompareRow label={c("compare.row.best_gas", "Meilleur prix gaz (kWh)")}>
                        {items.map((s) => {
                            const gas = (s.offers || []).filter((o) => o.energy_type === "gas");
                            const min = gas.length ? Math.min(...gas.map((o) => o.price_per_kwh)) : null;
                            const isBest = min !== null && cheapestGas !== null && min === cheapestGas;
                            return (
                                <div key={`pg-${s.id}`} className="px-5 py-4 border-l border-border">
                                    <div className="flex items-center gap-2">
                                        <PriceCell value={min} />
                                        {isBest && <Crown className="h-4 w-4 text-accent fill-accent" />}
                                    </div>
                                </div>
                            );
                        })}
                    </CompareRow>

                    {/* OFFERS COUNT - detailed only */}
                    {view === "detailed" && (
                    <CompareRow label={c("compare.row.offer_count", "Nombre d'offres")} isAlt>
                        {items.map((s) => (
                            <div key={`oc-${s.id}`} className="px-5 py-4 border-l border-border font-mono font-bold">{(s.offers || []).length}</div>
                        ))}
                    </CompareRow>
                    )}

                    {/* YEAR - detailed only */}
                    {view === "detailed" && (
                    <CompareRow label={c("compare.row.founded", "Année de fondation")}>
                        {items.map((s) => (
                            <div key={`y-${s.id}`} className="px-5 py-4 border-l border-border font-mono">{s.year_founded}</div>
                        ))}
                    </CompareRow>
                    )}

                    {/* IDEAL FOR - detailed only */}
                    {view === "detailed" && (
                    <CompareRow label={c("compare.row.best_for", "Idéal pour")} isAlt>
                        {items.map((s) => (
                            <div key={`if-${s.id}`} className="px-5 py-4 border-l border-border text-sm">{s.best_for}</div>
                        ))}
                    </CompareRow>
                    )}

                    {/* SERVICES - detailed only */}
                    {view === "detailed" && (
                    <CompareRow label={c("compare.row.services", "Services")}>
                        {items.map((s) => (
                            <div key={`sv-${s.id}`} className="px-5 py-4 border-l border-border">
                                <ul className="space-y-1.5">
                                    {(s.services || []).slice(0, 5).map((sv) => (
                                        <li key={sv} className="text-xs flex gap-1.5"><BadgeCheck className="h-3.5 w-3.5 text-primary shrink-0 mt-0.5" />{sv}</li>
                                    ))}
                                </ul>
                            </div>
                        ))}
                    </CompareRow>
                    )}

                    {/* ACTIONS */}
                    <CompareRow label={c("compare.row.actions", "Actions")} isAlt>
                        {items.map((s) => (
                            <div key={`a-${s.id}`} className="px-5 py-4 border-l border-border flex flex-col gap-2">
                                <Button asChild size="sm" className="rounded-full bg-primary hover:bg-primary/90 w-full" data-testid={`compare-detail-${s.slug}`}>
                                    <Link to={`/fournisseurs/${s.slug}`}>{c("compare.action.detail", "Fiche complète")} <ArrowRight className="h-3.5 w-3.5 ml-1" /></Link>
                                </Button>
                                <Button asChild size="sm" variant="outline" className="rounded-full w-full" data-testid={`compare-offers-${s.slug}`}>
                                    <Link to="/comparateur">{c("compare.action.offers", "Offres")}</Link>
                                </Button>
                                {s.website && (
                                    <Button asChild size="sm" variant="ghost" className="rounded-full w-full" data-testid={`compare-website-${s.slug}`}>
                                        <a href={s.website} target="_blank" rel="noopener noreferrer">
                                            {c("compare.action.website", "Site")} <ExternalLink className="h-3 w-3 ml-1" />
                                        </a>
                                    </Button>
                                )}
                            </div>
                        ))}
                    </CompareRow>
                </div>
            </div>

            {/* SUMMARY: Pick a winner */}
            <Card className="rounded-3xl mt-10 bg-primary text-primary-foreground border-0" data-testid="compare-winner">
                <CardContent className="p-8 md:p-10">
                    {(() => {
                        const winner = [...items].sort((a, b) => Number(b.overall_rating) - Number(a.overall_rating))[0];
                        return (
                            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-6">
                                <div className="flex items-center gap-4">
                                    <div className="h-14 w-14 rounded-2xl bg-accent flex items-center justify-center">
                                        <Trophy className="h-7 w-7 text-accent-foreground" />
                                    </div>
                                    <div>
                                        <p className="text-xs tracking-[0.2em] uppercase text-accent">{c("compare.winner.eyebrow", "Notre choix")}</p>
                                        <h3 className="font-display text-3xl md:text-4xl font-black mt-1">{winner.name}</h3>
                                        <p className="text-primary-foreground/80 text-sm mt-1">
                                            {c("compare.winner.score_label", "Meilleure note globale :")} <span className="font-mono font-bold text-accent">{Number(winner.overall_rating).toFixed(1)}/5</span>
                                        </p>
                                    </div>
                                </div>
                                <Button asChild className="rounded-full bg-accent text-accent-foreground hover:bg-accent/90 font-bold h-12 px-7" data-testid="winner-cta">
                                    <Link to={`/fournisseurs/${winner.slug}`}>{c("compare.winner.cta", "Voir la fiche complète")} <ArrowRight className="h-4 w-4 ml-2" /></Link>
                                </Button>
                            </div>
                        );
                    })()}
                </CardContent>
            </Card>
        </div>
    );
}

function CompareRow({ label, isAlt = false, children }) {
    return (
        <>
            <div className={`px-5 py-4 border-t border-border font-display font-semibold text-sm ${isAlt ? "bg-muted/40" : ""}`}>
                {label}
            </div>
            {Array.isArray(children) ? children.map((child, i) =>
                <div key={`compare-${label}-${i}`} className={`border-t border-border ${isAlt ? "bg-muted/40" : ""}`} style={{ display: "contents" }}>{child}</div>
            ) : children}
        </>
    );
}
